// The Monaco-facing wrapper around `collectTreeSitterSyntaxMarkers` — owns the
// version-capture / stale-drop contract so SqlMonaco stays thin and the drop
// behavior is unit-testable without Monaco (the perf test asserts it).
//
// Return contract:
//   • `readonly SyntaxMarker[]` — fresh result for the CURRENT model version;
//     the caller converts offsets via `model.getPositionAt` and paints.
//   • `null` — the buffer changed underneath the parse (stale) → the caller
//     must leave the currently-painted markers alone; the edit's own debounced
//     re-run paints the correct set.
//
// Idle gating (debounce, one-in-flight coalescing) lives in the SqlMonaco
// effect; the source-size cap lives here so every caller gets it.

import type { TreeSitterSyntaxEngineId } from './collectTreeSitterSyntaxMarkers';
import { collectTreeSitterSyntaxMarkers } from './collectTreeSitterSyntaxMarkers';
import type { SyntaxMarker } from './syntaxMarker';

/** Cloned from the mssql `sql-syntax` pass — skip pathologically large buffers. */
const MAX_SOURCE_LEN = 200_000;

/** Minimal `ITextModel` projection — `getVersionId` bumps on every edit. */
export type SyntaxPassModel = {
  getValue(): string;
  getVersionId(): number;
};

export async function runTreeSitterSyntaxPass(
  model: SyntaxPassModel,
  engineId: TreeSitterSyntaxEngineId,
): Promise<readonly SyntaxMarker[] | null> {
  const source = model.getValue();
  if (source.length > MAX_SOURCE_LEN) return [];

  // Capture BEFORE the async (WASM parse) gap; a keystroke during the parse
  // invalidates the result — never paint markers computed for stale text.
  const versionAtStart = model.getVersionId();
  const markers = await collectTreeSitterSyntaxMarkers({ source, engineId });
  if (model.getVersionId() !== versionAtStart) return null;
  return markers;
}

// ── Cancellation-aware coalescing runner (F4) ──────────────────────────────
// Owns the debounce / one-in-flight-coalescing / effect-cancellation bookkeeping
// so the SqlMonaco effect stays thin and the "no paint AND no reschedule after
// cleanup" contract is unit-testable without Monaco. A single cold WASM parse
// can resolve AFTER the effect is torn down (setting disabled / connection
// switched) — the captured `cancelled` flag guards BOTH the paint (via the
// `isLive()` the pass MUST check before painting) and the queued reschedule.

export type LiveSyntaxPassRunner = {
  /** Debounced trigger. No-op once cancelled. */
  schedule(): void;
  /** Run immediately, bypassing the debounce (the worker-carried mssql initial
   *  pass, whose parse is off-thread). */
  runNow(): void;
  /** Effect cleanup — cancels in-flight paints AND any pending/queued rerun. */
  cancel(): void;
};

export type CreateLiveSyntaxPassRunnerOptions = {
  readonly debounceMs: number;
  /** The compute+paint step. It MUST call `isLive()` immediately before painting
   *  and bail if it reads `false` (the effect was torn down mid-parse). */
  readonly run: (isLive: () => boolean) => Promise<void>;
  readonly setTimeout: (fn: () => void, ms: number) => unknown;
  readonly clearTimeout: (handle: unknown) => void;
};

export function createLiveSyntaxPassRunner(
  opts: CreateLiveSyntaxPassRunnerOptions,
): LiveSyntaxPassRunner {
  let inFlight = false;
  let rerunQueued = false;
  let cancelled = false;
  let timer: unknown = null;
  const isLive = (): boolean => !cancelled;

  const recompute = async (): Promise<void> => {
    if (cancelled) return;
    if (inFlight) {
      rerunQueued = true;
      return;
    }
    inFlight = true;
    try {
      await opts.run(isLive);
    } finally {
      inFlight = false;
      // A rerun queued while this pass ran must NOT reschedule after cleanup.
      if (rerunQueued && !cancelled) {
        rerunQueued = false;
        schedule();
      }
    }
  };

  const schedule = (): void => {
    if (cancelled) return;
    if (timer !== null) opts.clearTimeout(timer);
    timer = opts.setTimeout(() => {
      timer = null;
      void recompute();
    }, opts.debounceMs);
  };

  const runNow = (): void => {
    void recompute();
  };

  const cancel = (): void => {
    cancelled = true;
    if (timer !== null) {
      opts.clearTimeout(timer);
      timer = null;
    }
  };

  return { schedule, runNow, cancel };
}
